import type { AtlasViewerMode, Prototype } from '@atlas/api-contract';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink } from 'lucide-react';

import { ThemeToggle } from '@/components/theme/theme-toggle';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { PrototypeVersionSummary } from '@/lib/api/versions';

import { CommentModeToggle } from './comment-mode-toggle';
import { ScreenSelect } from './screen-select';
import { VersionDrawer } from './version-drawer';
import { ViewerMenu } from './viewer-menu';
import { WidthPresetControl } from './width-preset-control';
import type { ViewerWidthPresetId } from './width-presets';

export interface ViewerToolbarProps {
  readonly prototype: Prototype;
  /** Owning project's name, shown as a crumb; `null` until it resolves. */
  readonly projectName: string | null;
  readonly versions: readonly PrototypeVersionSummary[];
  readonly versionsLoading: boolean;
  readonly selectedVersionId: string | null;
  readonly onSelectVersion: (versionId: string) => void;

  readonly screenPaths: readonly string[];
  readonly currentScreenPath: string | null;
  readonly onSelectScreen: (screenPath: string) => void;

  readonly widthPreset: ViewerWidthPresetId;
  readonly onWidthPresetChange: (preset: ViewerWidthPresetId) => void;

  readonly mode: AtlasViewerMode;
  readonly onModeChange: (mode: AtlasViewerMode) => void;
  /** The agent has not answered, so mode and navigation cannot be honoured. */
  readonly agentUnavailable: boolean;

  /** Raw URL of the current screen on the sandbox origin, or `null`. */
  readonly rawUrl: string | null;

  /** Whether the current role may upload a version (spec matrix). */
  readonly canUpload: boolean;
  /** Whether the current role may edit or delete the prototype (spec matrix). */
  readonly canModify: boolean;

  readonly onEdit: () => void;
  readonly onUpload: () => void;
  readonly onDelete: () => void;
}

/**
 * The viewer's single bar of chrome (spec R3.3).
 *
 * One row, 48 pixels, a hairline underneath and no fill of its own beyond the
 * `viewer-chrome` token — the same restraint as the application header, held to
 * harder because here it is sitting directly on top of the thing under review.
 * Controls are small, unfilled and monochrome; the only element allowed to
 * carry a colour is the comment toggle when it is on, because that is the one
 * state where the reviewer needs to know at a glance that their next click
 * means something different.
 *
 * The back arrow leads to the project rather than to browser history: the
 * viewer is routinely arrived at from a pasted link, where "back" means leaving
 * Atlas entirely.
 */
export const ViewerToolbar = ({
  prototype,
  projectName,
  versions,
  versionsLoading,
  selectedVersionId,
  onSelectVersion,
  screenPaths,
  currentScreenPath,
  onSelectScreen,
  widthPreset,
  onWidthPresetChange,
  mode,
  onModeChange,
  agentUnavailable,
  rawUrl,
  canUpload,
  canModify,
  onEdit,
  onUpload,
  onDelete,
}: ViewerToolbarProps) => (
  <header className="flex h-12 shrink-0 items-center gap-2 border-b border-viewer-chrome-border bg-viewer-chrome px-3">
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          asChild
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 text-viewer-chrome-foreground hover:text-foreground"
        >
          <Link
            to="/projects/$projectId"
            params={{ projectId: prototype.projectId }}
          >
            <ArrowLeft aria-hidden="true" />
            <span className="sr-only">Back to the project</span>
          </Link>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">Back to the project</TooltipContent>
    </Tooltip>

    <nav
      aria-label="Breadcrumb"
      className="flex min-w-0 items-center gap-1.5 text-sm"
    >
      <Link
        to="/projects/$projectId"
        params={{ projectId: prototype.projectId }}
        className="shrink-0 truncate font-medium text-viewer-chrome-foreground hover:text-foreground"
      >
        {projectName ?? 'Project'}
      </Link>
      <span aria-hidden="true" className="text-viewer-chrome-foreground/50">
        /
      </span>
      <h1 className="min-w-0 truncate font-medium text-foreground">
        {prototype.name}
      </h1>
    </nav>

    <VersionDrawer
      versions={versions}
      selectedVersionId={selectedVersionId}
      isLoading={versionsLoading}
      onSelect={onSelectVersion}
    />

    <div className="ml-auto flex items-center gap-2">
      <ScreenSelect
        screenPaths={screenPaths}
        value={currentScreenPath}
        onChange={onSelectScreen}
        disabled={agentUnavailable}
      />

      <WidthPresetControl value={widthPreset} onChange={onWidthPresetChange} />

      <Separator orientation="vertical" className="h-5" />

      <CommentModeToggle
        mode={mode}
        onChange={onModeChange}
        disabled={agentUnavailable}
      />

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            asChild={rawUrl !== null}
            variant="ghost"
            size="icon"
            disabled={rawUrl === null}
            className="size-8 text-viewer-chrome-foreground hover:text-foreground"
          >
            {rawUrl === null ? (
              <>
                <ExternalLink aria-hidden="true" />
                <span className="sr-only">Open the prototype in a new tab</span>
              </>
            ) : (
              <a href={rawUrl} target="_blank" rel="noreferrer noopener">
                <ExternalLink aria-hidden="true" />
                <span className="sr-only">Open the prototype in a new tab</span>
              </a>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          Open in a new tab, without Atlas
        </TooltipContent>
      </Tooltip>

      <ThemeToggle />

      {canUpload || canModify ? (
        <ViewerMenu
          prototypeName={prototype.name}
          canUpload={canUpload}
          canModify={canModify}
          onEdit={onEdit}
          onUpload={onUpload}
          onDelete={onDelete}
        />
      ) : null}
    </div>
  </header>
);
